import { faFile, faGears } from "@fortawesome/pro-solid-svg-icons"
import { Typography } from "@mui/material"
import { translate } from "app/language/service"
import { deepCopy } from "app/tools/common"
import { CURRENCIES } from "currency/model"
import { FieldCheckbox } from "form/components/fields/FieldCheckbox"
import { FieldDate } from "form/components/fields/FieldDate"
import { FieldMoney } from "form/components/fields/FieldMoney"
import { FieldSelect } from "form/components/fields/FieldSelect"
import { FieldText } from "form/components/fields/FieldText"
import { Formik } from "formik"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { MenuItem } from "ui/components/main-layout/MenuItem"
import { openColorPickerPopup } from "ui/tools/color-picker-popup"
import { addImportant } from "ui/tools/common"
import {
  IUIConfiguration,
  IUIConfigurationColor,
  IUIConfigurationColors,
  UI_CONFIG_FONT_DIV_ID,
  UI_CONFIG_PAGE_DIV_ID
} from "../model"
import { getDarkOrLightColor } from "../tools/common"
import { getBoxShadow } from "../tools/theme"
import { setTempUiConfigUndoRedo } from "../tools/undo-redo"

interface IUiConfigPreviewProps {
  isDark: boolean
  uiConfig: IUIConfiguration
  isPhone: boolean
}

const getColor = (color: IUIConfigurationColor, isDarkPreview: boolean, hexColor: string): IUIConfigurationColor => {
  color = deepCopy(color)
  if (isDarkPreview) {
    color.dark = hexColor
  } else {
    color.light = hexColor
  }
  return color
}

export const UiConfigPreview = React.memo(({ isDark, uiConfig, isPhone }: IUiConfigPreviewProps) => {
  const getEditColorFunction =
    (property: keyof IUIConfigurationColors) =>
    (event: any): any => {
      event.stopPropagation()
      event.preventDefault()
      openColorPickerPopup({
        hexColor: getDarkOrLightColor(isDark, uiConfig.colors[property]),
        setHexColor: newColor =>
          setTempUiConfigUndoRedo({
            ...uiConfig,
            colors: {
              ...uiConfig.colors,
              [property]: getColor(uiConfig.colors[property], isDark, newColor)
            }
          }),
        title: translate(`admin.ui-configuration.set.color.${isDark ? "dark" : "light"}`)
      })
    }
  return (
    <FlexBox
      vertical
      fullWidth
      gap
      sx={{
        "p": "2rem",
        "backgroundColor": getDarkOrLightColor(isDark, uiConfig.colors.backgroundDefault),
        "& *": {
          fontFamily: addImportant(uiConfig.font.googleFamily)
        }
      }}>
      <FlexBox
        fullWidth
        gap
        wrap
        sx={{
          // "p": "1rem",
          "& > *": {
            width: isPhone ? "100%" : "calc(50% - .5rem)",
            height: "27rem",
            boxShadow: getBoxShadow(uiConfig, isDark)
          }
        }}>
        <FlexBox
          vertical
          gap
          jc="start"
          onClick={getEditColorFunction("backgroundDefault")}
          sx={{
            "p": "1rem",
            "backgroundColor": getDarkOrLightColor(isDark, uiConfig.colors.backgroundDefault),
            "border": `1px solid ${getDarkOrLightColor(isDark, uiConfig.colors.border)}`,
            "& > *": {
              cursor: "pointer"
            }
          }}>
          <Typography
            fontWeight="bold"
            sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.textPrimary) }}
            onClick={event => {
              event.stopPropagation()
              event.preventDefault()
              const container = document.getElementById(UI_CONFIG_PAGE_DIV_ID)
              if (!container) {
                return
              }
              const div = document.getElementById(UI_CONFIG_FONT_DIV_ID)
              if (!div) {
                return
              }
              container.scrollBy({ behavior: "smooth", top: div.offsetTop - 70 })
            }}>
            {translate("admin.ui-configuration.font.in.use", uiConfig.font.googleFamily)}
          </Typography>
          <FlexBox
            fullWidth
            sx={{
              width: "100%",
              height: "4rem",
              backgroundColor: getDarkOrLightColor(isDark, uiConfig.colors.primary)
            }}
            onClick={getEditColorFunction("primary")}>
            <Typography
              sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.textSpecial) }}
              onClick={getEditColorFunction("textSpecial")}>
              Special text
            </Typography>
          </FlexBox>
          <Typography
            sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.textPrimary) }}
            onClick={getEditColorFunction("textPrimary")}>
            Text Primary
          </Typography>
          <Typography
            sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.textSecondary) }}
            onClick={getEditColorFunction("textSecondary")}>
            Text Secondary
          </Typography>
          <Typography
            sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.textPrimary2) }}
            onClick={getEditColorFunction("textPrimary2")}>
            Text Primary2
          </Typography>
          <Typography
            sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.textDisabled) }}
            onClick={getEditColorFunction("textDisabled")}>
            Text disabled
          </Typography>
          <Typography
            sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.error) }}
            onClick={getEditColorFunction("error")}>
            Error message
          </Typography>
        </FlexBox>
        <FlexBox
          vertical
          gap
          onClick={getEditColorFunction("backgroundPaper")}
          sx={{
            "p": "1rem",
            "backgroundColor": getDarkOrLightColor(isDark, uiConfig.colors.backgroundPaper),
            "border": `1px solid ${getDarkOrLightColor(isDark, uiConfig.colors.border)}`,
            "& > *": {
              cursor: "pointer"
            }
          }}>
          <FlexBox
            fullWidth
            sx={{
              width: "100%",
              height: "4rem",
              backgroundColor: getDarkOrLightColor(isDark, uiConfig.colors.primary)
            }}
            onClick={getEditColorFunction("primary")}>
            <Typography
              sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.textSpecial) }}
              onClick={getEditColorFunction("textSpecial")}>
              Special text
            </Typography>
          </FlexBox>
          <Typography
            sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.textPrimary) }}
            onClick={getEditColorFunction("textPrimary")}>
            Text Primary
          </Typography>
          <Typography
            sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.textSecondary) }}
            onClick={getEditColorFunction("textSecondary")}>
            Text Secondary
          </Typography>
          <Typography
            sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.textPrimary2) }}
            onClick={getEditColorFunction("textPrimary2")}>
            Text Primary2
          </Typography>
          <Typography
            sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.textDisabled) }}
            onClick={getEditColorFunction("textDisabled")}>
            Text disabled
          </Typography>
          <Typography
            sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.error) }}
            onClick={getEditColorFunction("error")}>
            Error message
          </Typography>
          <MenuItem
            menu={{ titleKey: "admin.ui-configuration.menu.active", pagePaths: [], icon: faGears }}
            isActive
            isOpened
            isFirst
            sx={{
              background: getDarkOrLightColor(isDark, uiConfig.colors.primary),
              color: getDarkOrLightColor(isDark, uiConfig.colors.textSpecial),
              cursor: "pointer"
            }}
            onBackgroundClick={getEditColorFunction("primary")}
            onTextClick={getEditColorFunction("textSpecial")}
          />
          <MenuItem
            menu={{ titleKey: "admin.ui-configuration.menu.inactive", pagePaths: [], icon: faFile }}
            isActive={false}
            isOpened
            isFirst
            sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.textPrimary) }}
            onBackgroundClick={getEditColorFunction("primary")}
            onTextClick={getEditColorFunction("textPrimary")}
          />
        </FlexBox>
      </FlexBox>
      <FlexBox
        vertical
        gap
        fullWidth
        onClick={getEditColorFunction("backgroundDefault")}
        sx={{
          "boxShadow": getBoxShadow(uiConfig, isDark),
          "backgroundColor": getDarkOrLightColor(isDark, uiConfig.colors.backgroundDefault),
          "border": `1px solid ${getDarkOrLightColor(isDark, uiConfig.colors.border)}`,
          ".MuiSwitch-colorPrimary.Mui-checked": {
            color: getDarkOrLightColor(isDark, uiConfig.colors.textPrimary2)
          },
          ".MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
            backgroundColor: getDarkOrLightColor(isDark, uiConfig.colors.textPrimary2)
          },
          "&.Mui-disabled": {
            color: getDarkOrLightColor(isDark, uiConfig.colors.borderDisabled)
          },
          ".MuiCheckbox": {
            "color": getDarkOrLightColor(isDark, uiConfig.colors.textPrimary2),
            "&.Mui-checked, &.MuiCheckbox-indeterminate": {
              color: getDarkOrLightColor(isDark, uiConfig.colors.textPrimary2)
            },
            "&.Mui-disabled": {
              color: getDarkOrLightColor(isDark, uiConfig.colors.borderDisabled)
            }
          },
          "& fieldset": {
            border: `1px solid ${getDarkOrLightColor(isDark, uiConfig.colors.border)}`
          },
          ".MuiFormControl": {
            "& .MuiInputBase-root.MuiOutlinedInput-root.Mui-focused fieldset": {
              borderColor: getDarkOrLightColor(isDark, uiConfig.colors.form)
            },
            "& .MuiFormLabel-root.MuiInputLabel-root.Mui-focused": {
              color: getDarkOrLightColor(isDark, uiConfig.colors.form)
            },
            "& .MuiFormControlLabel-label": {
              color: getDarkOrLightColor(isDark, uiConfig.colors.textPrimary)
            }
          },
          "& .field-money": {
            "& .icon": {
              color: getDarkOrLightColor(isDark, uiConfig.colors.textSecondary)
            },
            "& .icon:hover": {
              color: getDarkOrLightColor(isDark, uiConfig.colors.textPrimary)
            },
            ".field-money-currency": {
              "backgroundColor": getDarkOrLightColor(isDark, uiConfig.colors.form),
              "& > p": {
                color: getDarkOrLightColor(isDark, uiConfig.colors.textForm)
              }
            }
          },
          "& .Mui-disabled": {
            "color": getDarkOrLightColor(isDark, uiConfig.colors.textDisabled),
            "& fieldset": {
              border: `1px solid ${getDarkOrLightColor(isDark, uiConfig.colors.borderDisabled)}`
            }
          }
        }}>
        <Formik initialValues={{}} validate={() => undefined} onSubmit={() => undefined}>
          <FlexBox vertical gap fullWidth sx={{ p: "1rem" }}>
            <FlexBox
              gap
              wrap
              sx={{
                "& #checkbox-checked span.MuiFormControlLabel-label, & #checkbox-unchecked span.MuiFormControlLabel-label":
                  {
                    color: getDarkOrLightColor(isDark, uiConfig.colors.textPrimary)
                  }
              }}>
              <FieldCheckbox
                label="Checked"
                fieldName="checkbox-checked"
                value={true}
                onSwitchClick={getEditColorFunction("textPrimary2")}
                onLabelClick={getEditColorFunction("textPrimary")}
              />
              <FieldCheckbox
                label="Unchecked"
                fieldName="checkbox-unchecked"
                value={false}
                onLabelClick={getEditColorFunction("textPrimary")}
              />
              <FieldCheckbox
                label="Checked disabled"
                fieldName="checkbox-disabled"
                value={true}
                disabled
                onSwitchClick={getEditColorFunction("textPrimary2")}
                onLabelClick={getEditColorFunction("textDisabled")}
              />
              <FieldCheckbox
                label="Unchecked disabled"
                fieldName="checkbox-unchecked-disabled"
                value={false}
                disabled
                onLabelClick={getEditColorFunction("textDisabled")}
              />
            </FlexBox>
            <FlexBox gap wrap>
              <FieldText label="Text" fieldName="text-filled" clearable value={"Filled"} />
              <FieldText label="Text disabled" fieldName="text-disabled" value={"Disabled"} disabled />
            </FlexBox>
            <FlexBox gap wrap>
              <FieldDate label="Date" fieldName="date" value={new Date()} />
              <FieldDate label="Date disabled" fieldName="date-disabled" value={new Date()} disabled />
            </FlexBox>
            <FlexBox gap wrap>
              <FieldMoney
                label="Money"
                fieldName="money"
                value={27.5}
                currencyId={CURRENCIES.USD}
                onBoxClick={getEditColorFunction("form")}
                onTextClick={getEditColorFunction("textForm")}
              />
              <FieldMoney
                label="Money disabled"
                fieldName="money-disabled"
                value={27.5}
                currencyId={CURRENCIES.USD}
                disabled
                onBoxClick={getEditColorFunction("form")}
                onTextClick={getEditColorFunction("textForm")}
              />
            </FlexBox>
            <FlexBox gap wrap>
              <FieldSelect
                label="Select"
                fieldName="select"
                options={["First entry", "Second entry", "Third entry"]}
                value={"First entry"}
                optionLabelFn={l => String(l)}
              />
              <FieldSelect
                label="Select disabled"
                fieldName="select"
                options={["First entry", "Second entry", "Third entry"]}
                value={"First entry"}
                disabled
                optionLabelFn={l => String(l)}
              />
            </FlexBox>
          </FlexBox>
        </Formik>
      </FlexBox>
    </FlexBox>
  )
})
